import React from 'react';
import { Modal, Stack, Text, Group, Badge, Divider, List, ThemeIcon, Button } from '@mantine/core';
import { IconTarget, IconShield, IconAlertTriangle, IconCheck, IconInfoCircle } from '@tabler/icons-react';

/**
 * AIScoreExplanationModal - Display detailed AI score calculation explanation
 * Shows how the AI reached its conclusion for risk severity, mitigation quality, or security threat level
 */
export default function AIScoreExplanationModal({ opened, onClose, scoreType, scoreData }) {
  if (!scoreData) return null;

  // Configuration for each score type
  const config = {
    riskSeverity: {
      title: 'Risk Severity Assessment',
      icon: IconTarget,
      color: scoreData.level === 'HIGH' ? 'red' : scoreData.level === 'MEDIUM' ? 'orange' : 'green',
      iconComponent: <IconTarget size={24} />
    },
    mitigationQuality: {
      title: 'Mitigation Quality Assessment',
      icon: IconShield,
      color: scoreData.level === 'EFFECTIVE' ? 'green' : scoreData.level === 'MODERATE' ? 'yellow' : 'red',
      iconComponent: <IconShield size={24} />
    },
    securityThreat: {
      title: 'Security Threat Level Assessment',
      icon: IconAlertTriangle,
      color: scoreData.level === 'CRITICAL' ? 'red' : scoreData.level === 'HIGH' ? 'orange' : scoreData.level === 'MEDIUM' ? 'yellow' : 'green',
      iconComponent: <IconAlertTriangle size={24} />
    }
  };

  const currentConfig = config[scoreType] || config.riskSeverity;

  // Parse explanation into bullet points
  const explanationParts = scoreData.explanation.split('. ').filter(Boolean);

  // Get recommendation based on score type and level
  const getRecommendation = () => {
    if (scoreType === 'riskSeverity') {
      if (scoreData.level === 'HIGH') {
        return 'Immediate action required: Prioritize mitigation of Critical and High priority risks. Consider risk transfer or avoidance strategies for the most severe threats.';
      } else if (scoreData.level === 'MEDIUM') {
        return 'Action recommended: Develop and implement mitigation plans for High and Significant risks. Monitor Critical risks closely.';
      } else {
        return 'Maintain vigilance: Continue monitoring existing risks and implementing planned controls. Regular review recommended.';
      }
    } else if (scoreType === 'mitigationQuality') {
      if (scoreData.level === 'EFFECTIVE') {
        return 'Strong mitigation posture: Continue implementing and monitoring planned controls. Regularly review residual risk levels.';
      } else if (scoreData.level === 'MODERATE') {
        return 'Improvement needed: Increase control coverage for risks without mitigation plans. Evaluate control effectiveness and residual risk levels.';
      } else {
        return 'Critical attention required: Significant gaps in mitigation coverage. Prioritize developing and implementing controls for high-priority risks.';
      }
    } else if (scoreType === 'securityThreat') {
      if (scoreData.level === 'CRITICAL' || scoreData.level === 'HIGH') {
        return 'Urgent security review required: Address Critical and High severity threats immediately. Implement additional security controls and monitoring.';
      } else if (scoreData.level === 'MEDIUM') {
        return 'Security improvements recommended: Review threat scenarios and implement appropriate security controls. Consider threat modeling exercises.';
      } else {
        return 'Maintain security posture: Continue monitoring for emerging threats. Regular security assessments recommended.';
      }
    }
    return '';
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="md">
          <ThemeIcon size="lg" variant="light" color={currentConfig.color}>
            {currentConfig.iconComponent}
          </ThemeIcon>
          <Text fw={600} size="lg">{currentConfig.title}</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="lg">
        {/* Score Display */}
        <Group justify="center" gap="xl">
          <Stack gap={4} align="center">
            <Badge size="xl" variant="filled" color={currentConfig.color}>
              {scoreData.level}
            </Badge>
            <Text size="sm" c="dimmed">Assessment Level</Text>
          </Stack>
          <Stack gap={4} align="center">
            <Text size="2xl" fw={700} c={currentConfig.color}>
              {scoreData.score}/100
            </Text>
            <Text size="sm" c="dimmed">Confidence Score</Text>
          </Stack>
        </Group>

        <Divider label="How AI Calculated This" labelPosition="center" />

        {/* Explanation Breakdown */}
        <Stack gap="sm">
          <Group gap="xs">
            <IconInfoCircle size={18} color="gray" />
            <Text fw={600} size="sm">Calculation Methodology</Text>
          </Group>
          <List
            spacing="xs"
            size="sm"
            icon={
              <ThemeIcon color="blue" size={20} radius="xl" variant="light">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            {explanationParts.map((part, index) => (
              <List.Item key={index}>
                <Text size="sm">{part.trim()}.</Text>
              </List.Item>
            ))}
          </List>
        </Stack>

        <Divider />

        {/* Recommendation */}
        <Stack gap="sm">
          <Group gap="xs">
            <IconAlertTriangle size={18} color={currentConfig.color} />
            <Text fw={600} size="sm">AI Recommendation</Text>
          </Group>
          <Text size="sm" c="dimmed">
            {getRecommendation()}
          </Text>
        </Stack>

        {/* Close Button */}
        <Group justify="flex-end">
          <Button onClick={onClose} variant="light">
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
